import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { Store } from '@ngrx/store'
import { State } from '@app/core/store'
import { Router, ActivatedRoute } from '@angular/router'
import { Observable, Subject, of, ReplaySubject, BehaviorSubject } from 'rxjs'
import { filter, switchMap, takeUntil } from 'rxjs/operators'

import {
  logOut,
  logIn,
  logInFailed,
} from '@app/auth/store/actions/auth.actions'
import { environment } from '@env/environment'
import { LocalService } from '@app/shared/services/local.service'
const BASE_PATH = environment.basePath

/**
 * The main security class that authenticates and disconnects the user, checks if the user is `logged on` or `not` and
 * provides the user's roles and name.
 *
 * This class should not be modified directly, you can provide your own service which should extend the default one as follows:
 ```
  export class MyAuthManagerService extends AuthManagerService {
   ...
  }
 ```
 * And provide your class in the `core/custom-core.module.ts` file as follows:
 ```
  providers: [
    { provide: AuthManagerService, useClass: MyAuthManagerService }
 ]
 ```
 * */
@Injectable({
  providedIn: 'root',
})
export class AuthManagerService {

  roles = JSON.parse(this.secureStorage.getJsonValue('permissions'))
  permissions = this.secureStorage.getJsonValue('permissions')
  // roles = [{ "id": "a2e6d28b-4425-4a6c-98fe-3d6dbc558996", "name": "admin", "type": "Permissões de sistema" }, { "id": "a2e6d28b-4425-4a6c-98fe-3d6dbcef29de", "name": "Consultar fundos", "type": "Permissões de sistema" }, { "id": "db526406-6d90-479b-a52a-4f98e5c88d32", "name": "Ativos - Operações", "type": "Permissões de sistema" }, { "id": "2942fc41-3503-404b-858a-753ddfbe3631", "name": "Ativos - Relatórios", "type": "Permissões de sistema" }, { "id": "63efa4ef-df6d-4d4b-a233-4e7df6f412e7", "name": "Definir perfil de acesso", "type": "Permissões de sistema" }, { "id": "d0f92540-0998-11eb-bc9a-000000000000", "name": "Criar Perfil de acesso", "type": "Permissões de sistema" }, { "id": "7816c928-33b4-4f0f-9b04-a39ea3008674", "name": "Operações - Lay-outs SC", "type": "Permissões de sistema" }, { "id": "3fcb26e5-c442-4f85-9d9d-60be76c8cbbc", "name": "Operações - Boletas", "type": "Permissões de sistema" }, { "id": "e209bca0-0998-11eb-bc9a-000000000000", "name": "Cadastrar usuários", "type": "Permissões de sistema" }, { "id": "4f8cb5c0-0999-11eb-bc9a-000000000000", "name": "Editar perfil de acesso", "type": "Permissões de sistema" }, { "id": "031da701-8eb9-477a-a342-3e6c1e1aea3f", "name": "Ativos - Contratos e Documentos", "type": "Permissões de sistema" }, { "id": "e123a86a-7e45-4526-b810-83f56758c5c0", "name": "Ativos - Ajuda", "type": "Permissões de sistema" }, { "id": "b1a1f0ee-e190-47ef-aa34-d0e994eac4d2", "name": "Operações - Boletagem Manual", "type": "Permissões de sistema" }, { "id": "15e10bbb-a6e4-4b14-90ae-3f910b5793e6", "name": "Criar grupo de investimento", "type": "Permissões de sistema" }, { "id": "756f65fa-9af8-499e-950b-61326578a45c", "name": "Excluir grupo de investimento", "type": "Permissões de sistema" }, { "id": "99df647f-a141-4f7f-928b-395e7f2a6c42", "name": "Excluir perfil de acesso", "type": "Permissões de sistema" }, { "id": "5a6ad21d-4efd-47c1-9791-58fcbb0f1830", "name": "Acessar dashboard", "type": "Permissões de sistema" }, { "id": "da00c67a-b90f-41ac-94cf-ea06858a3239", "name": "Editar usuários", "type": "Permissões de sistema" }, { "id": "6b6cc8a7-48c1-4ef2-a3ef-9184d92fcd1f", "name": "Consultar grupos de investimentos", "type": "Permissões de sistema" }, { "id": "16f10bbb-a6e4-4b14-90ae-3f910b5793e6", "name": "Associar Fundo a Grupo de Investimento", "type": "Permissões de sistema" }, { "id": "14e10bbb-a6e4-4b14-90ae-3f910b5793e6", "name": "Editar grupo de investimento", "type": "Permissões de sistema" }, { "id": "13e10bbb-a6e4-4b14-90ae-3f910b5793e6", "name": "Definir grupo de investimento", "type": "Permissões de sistema" }, { "id": "a3e6d28b-4425-4a6c-98fe-3d6dbcef29df", "name": "Editar fundo", "type": "Permissões de sistema" }, { "id": "b9211a1b-75d0-49d7-a329-aad12e5ef543", "name": "Operações - Upload de Boleta", "type": "Permissões de sistema" }, { "id": "c568362b-75aa-4754-b29a-6a494d779378", "name": "Dash - Fluxo de caixa do fundo", "type": "Permissões de operação" }, { "id": "c518362b-75aa-4754-b29a-6a494d779378", "name": "Dash - PL do fundo", "type": "Permissões de operação" }, { "id": "7545c00b-72b1-cc68-97fa-cb6306e121b6", "name": "Dash - Operações", "type": "Permissões de operação" }, { "id": "8145c00b-72b1-cc68-97fa-cb6306e121c7", "name": "Dash - Operações do fundo", "type": "Permissões de operação" }, { "id": "d0921181-2d65-4764-bd28-82cb8057e3c7", "name": "Boletagem Manual - Bloqueio/desbloqueio de Fundos", "type": "Permissões de operação" }, { "id": "42d603c8-9097-44a1-aa79-afc2a3887eed", "name": "Boletagem Manual - Bloqueio/desbloqueio de Renda Fixa", "type": "Permissões de operação" }, { "id": "d2d53387-7a5e-426a-b37e-c4bd55900772", "name": "Boletagem Manual - Bloqueio/desbloqueio de Renda variável", "type": "Permissões de operação" }, { "id": "a287e9ba-df15-428f-a9b9-d52842f74388", "name": "Boletagem Manual - Cadastro de margem requerida", "type": "Permissões de operação" }, { "id": "c508362b-75aa-4754-b29a-6a494d779378", "name": "Dash - Caixa on line", "type": "Permissões de operação" }, { "id": "0f18da47-1c67-4e95-be2f-886037dd0422", "name": "Boletagem Manual - Cadastro de Opções Flexíveis", "type": "Permissões de operação" }, { "id": "6e71d786-4e00-4fef-8c0c-03d853b3c647", "name": "Boletagem Manual - Compromissadas", "type": "Permissões de operação" }, { "id": "9ad552d9-0cdb-4136-80ce-524c3d52f4d0", "name": "Boletagem Manual - Despesas/receitas", "type": "Permissões de operação" }, { "id": "a98a8cee-124b-428e-a0e2-377b14952de8", "name": "Boletagem Manual - Empréstimos de ações", "type": "Permissões de operação" }, { "id": "7d853d6d-ceb4-46e0-aeb1-bd5a37f435bd", "name": "Boletagem Manual - Exercício de opções de futuros", "type": "Permissões de operação" }, { "id": "6979a3e3-7a1b-43e2-b32c-3a5eeb614f71", "name": "Boletagem Manual - Exercício de opções de Renda variável", "type": "Permissões de operação" }, { "id": "201d3af1-8be2-4fc9-8bf2-a5ef752c0f68", "name": "Boletagem Manual - Renda Fixa", "type": "Permissões de operação" }, { "id": "d0205945-0f3a-4562-8172-87b0d2957bbb", "name": "Boletagem Manual - Fundos de investimentos", "type": "Permissões de operação" }, { "id": "0ea9a217-3d61-4b64-a282-1276125fbc2d", "name": "Boletagem Manual - Futuros", "type": "Permissões de operação" }, { "id": "ae54d0e8-bf85-4883-94ab-3340e8824b97", "name": "Boletagem Manual - Ingresso/Retirada", "type": "Permissões de operação" }, { "id": "d11bb4aa-8cc8-45e0-acff-017df721fe8b", "name": "Boletagem Manual - Livre digitação", "type": "Permissões de operação" }, { "id": "7cfdfaaf-ce9c-486a-989c-b6ce0a5c0403", "name": "Boletagem Manual - Renda variável", "type": "Permissões de operação" }, { "id": "7d773ea4-5190-4937-8d58-d6a4e8832954", "name": "Boletagem Manual - Swap", "type": "Permissões de operação" }, { "id": "e2d5e085-5379-4056-8af2-5740188cac74", "name": "Boletagem Manual - Termo de ações", "type": "Permissões de operação" }, { "id": "c558362b-75aa-4754-b29a-6a494d779378", "name": "Dash - PL", "type": "Permissões de operação" }, { "id": "c85664a2-cfb4-40e6-b294-4f82dc2e639f", "name": "Dash - Informações do Fundo", "type": "Permissões de operação" }, { "id": "c407362b-75aa-4754-b29a-6a494d779378", "name": "Dash - Cotas do Fundo", "type": "Permissões de operação" }, { "id": "c507362b-75aa-4754-b29a-6a494d779378", "name": "Dash - Acumulado Gestão", "type": "Permissões de operação" }, { "id": "c615362b-75aa-4754-b29a-6a494d779378", "name": "Dash - Aprovar Cotas do Fundo", "type": "Permissões de operação" }, { "id": "c556362b-75aa-4754-b29a-6a494d779378", "name": "Dash - Rejeitar Cotas do Fundo", "type": "Permissões de operação" }, { "id": "8eecbc7c-8bf2-4869-8a7f-5ccf9b2c08c9", "name": "Upload - Bloqueio/desbloqueio de Fundos", "type": "Permissões de operação" }, { "id": "5efe3f63-96ee-4418-8956-adf3bf2ca3b5", "name": "Upload", "type": "Permissões de operação" }, { "id": "0d40b4d2-4141-4a7e-b7c9-7c4cef9ba589", "name": "Registrar Boleta Fixa Variavel", "type": "Permissões de operação" }, { "id": "b5c7a38b-4425-4a6c-98fe-3d6dbcef29ae", "name": "Cadastrar Boleta Manual Renda Variável", "type": "Permissões de operação" }, { "id": "6165c00b-72b1-cc68-97fa-cb6306e121a8", "name": "Dash - Operações", "type": "Permissões de relatório" }, { "id": "1446c019-553f-4ce9-98cf-96e5d5f042bc", "name": "Dash - Cotas do Fundo", "type": "Permissões de relatório" }, { "id": "8a9997a0-0999-11eb-bc9a-000000000000", "name": "Dash - Caixa on line", "type": "Permissões de relatório" }, { "id": "d55fbe40-0999-11eb-bc9a-000000000000", "name": "Dash - PL", "type": "Permissões de relatório" }, { "id": "f4bb6460-0999-11eb-bc9a-000000000000", "name": "Dash - Fluxo de caixa do fundo", "type": "Permissões de relatório" }, { "id": "04c401a0-099a-11eb-bc9a-000000000000", "name": "Dash - PL do fundo", "type": "Permissões de relatório" }, { "id": "f6bb6460-0999-11eb-bc9a-000000000000", "name": "Dash - Informações do Fundo", "type": "Permissões de relatório" }, { "id": "d66fbe40-0999-11eb-bc9a-000000000000", "name": "Dash - Acumulado Gestão", "type": "Permissões de relatório" }]
  /**
   * The source emitter for the client's brand name
   */
  brandSubject = new ReplaySubject<string>()
 
  resetPassword: boolean
  /**
   * Default constructor
   */
  constructor(
    private secureStorage: LocalService,
    protected store: Store<State>,
    protected activatedRoute: ActivatedRoute,
    protected http: HttpClient,
    protected router: Router
  ) {
    this.initBrandName()
  }
  /**
   
   * @param credential user to authenticate
   * @returns {Observable<any>} user token
   */
  authenticateUser(credential): void { }
  /**
   * Checks if the current user is `logged on` `not`
   * @return {boolean}
   */
  isLogged(): any {
    return true
  }
 
  isChangePasswordRequired(): boolean {
    return false
  }
  /**
   * Gets the user's roles
   * @return {array}
   */
  getRoles(): any {
    if (this.permissions) {
      return this.permissions ? JSON.parse(this.permissions).map(role => role.name) : [];

    } else {
      this.permissions = this.secureStorage.getJsonValue('permissions')
      return this.permissions ? JSON.parse(this.permissions).map(role => role.name) : [];

    }
  }
  /**
   * check if the current user is allowed
   * @param {object} rolesBase - roles base list
   * @return {string} boolean
   */
  isUserRolesHas(rolesBase: Set<String> = new Set(['admin'])): boolean {
    return this.getRoles().filter(role => rolesBase.has(role)).length > 0;
  }
  /**
   * Returns the access token
   * @returns {any} the access token if exists
   */
  getAccessToken(): any {
    return {}
  }
  /**
   * Clears the access and refresh tokens
   * @returns {void}
   */
  removeAccessToken(): void { }
  /**
   * Decodes the JWT access token
   */
  decodeToken(): any {
    return {}
  }
  /**
   * Gets the user's name
   * */
  getUserName(): string {
    return 'Guest'
  }
  /**
   * Gets the user's name
   * */
  getName(): string {
    return this.getUserName()
  }
  /**
   * Post the logout request using access token
   * @returns {Observable<any>}
   */
  logout(): void {
    this.doLogout()
  }
  /**
   * Logout the user
   */
  private doLogout() {
    //this.store.dispatch(logOut({}))
  }
  /**
   * Implement this method in a custom service to set the brand name for
   * your users from token, cookie, REST call...etc
   */
  initBrandName() {
    /**
    this.brandSubject.next('example');
    this.activatedRoute.queryParams.subscribe(
      params => {
        if (params['brand']) {
          this.brandSubject.next(params['brand']);
        }
      });
      */
  }
}
